// What a choice DOES, which differs by platform.
//
// The Settings UI is shared: one Theme control and one Language control serve a
// phone and a desktop. What happens when a person picks a value is not shared.
// A phone saves a preference in its own native storage and keeps a copy the
// first frame can reach; a desktop saves it in the browser. A phone switching
// language also moves PrimeVue's own words.
//
// So the stores call the actions held here, and a target swaps them in. The
// browser's are the default — what a desktop app, a Storybook and a unit test
// get — and `@hds/common-ui-lib/mobile` installs the phone's when it is
// imported. An app never touches this: which actions it gets follows from which
// target it imports.
//
// These are functions, not state. Two apps in one process share them and
// nothing else.

const THEME_KEY = 'aac.theme.v1';
const LANGUAGE_KEY = 'aac.locale.v1';

// The desktop portal saved its theme under its own key, with `os` where every
// other place says `system`. Read once, so nobody loses their choice.
const LEGACY_DESKTOP_THEME_KEY = 'administration-theme';
const LEGACY_SYSTEM_THEME = 'os';

// The browser's own storage, or nothing. Private mode, a full quota and a
// webview with storage switched off all throw, and none of them is a reason to
// stop: a preference that cannot be saved still applies for this session.
function readBrowser(key) {
    try {
        return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

function writeBrowser(key, value) {
    try {
        globalThis.localStorage?.setItem(key, value);
    } catch {
        // See above.
    }
}

function savedBrowserTheme() {
    const saved = readBrowser(THEME_KEY);
    if (saved) {
        return saved;
    }
    const legacy = readBrowser(LEGACY_DESKTOP_THEME_KEY);
    if (legacy === LEGACY_SYSTEM_THEME) {
        return 'system';
    }
    return legacy;
}

// Move an application's language: its vue-i18n, and the document's own tag.
function applyLanguage(application, language) {
    const i18n = application?.config.globalProperties.$i18n;
    if (i18n) {
        i18n.locale = language;
    }
    if (typeof document !== 'undefined') {
        document.documentElement.lang = language;
    }
}

const browserActions = {
    theme: {
        // What is known before the first frame. The browser's storage is
        // synchronous, so this is the saved choice itself.
        firstFrame: () => savedBrowserTheme(),
        load: async () => savedBrowserTheme(),
        save: async (theme) => writeBrowser(THEME_KEY, theme),
        rememberFirstFrame: () => {}
    },
    language: {
        load: async () => readBrowser(LANGUAGE_KEY),
        save: async (language) => writeBrowser(LANGUAGE_KEY, language),
        // A desktop keeps the language it was built with until a person picks
        // another: it does not follow the browser's.
        initial: () => null,
        apply: applyLanguage
    }
};

let installed = browserActions;

/** Swap in a platform's actions. Called by a target's index, once, on import. */
function installShellActions(actions) {
    installed = actions;
}

/** The actions for the platform this app was built for. */
function shellActions() {
    return installed;
}

export { applyLanguage, browserActions, installShellActions, shellActions, LANGUAGE_KEY, THEME_KEY };
